/** Upload stand-in after Summarize replaces a reference PDF with short text. */
export function referenceUploadFile(
  fileName: string,
  extractedText: string,
  file: File | null,
  summarized: boolean
): File {
  if (file && !summarized) return file;
  const base = fileName.replace(/\.pdf$/i, "").trim() || "reference";
  return new File([extractedText], `${base}.summary.txt`, { type: "text/plain" });
}

export function pdfSummaryNote(preserved: "role instructions" | "guidelines"): string {
  return `Summarize replaces this PDF with a short summary. Your ${preserved} stay unchanged.`;
}

export type UploadJobStatus = "summarizing" | "ready" | "failed";

export interface ReadySummaryJob {
  key: string;
  fileName: string;
  extractedText: string;
  status: UploadJobStatus;
  savedDocumentId?: string;
}

/** No jobs means the row has nothing in flight. Any unfinished job is Not ready. */
export function uploadReadiness(statuses: UploadJobStatus[]): "ready" | "not-ready" | null {
  if (statuses.length === 0) return null;
  return statuses.every((status) => status === "ready") ? "ready" : "not-ready";
}

interface TrackedPdf {
  key: string;
  fileName: string;
  extractedText: string;
  file: File | null;
  summarized: boolean;
}

/** Fold a finished background summary into the PDF row that started it. */
export function applyReadySummaries<T extends TrackedPdf>(docs: T[], jobs: ReadySummaryJob[]): T[] {
  let changed = false;
  const next = docs.map((doc) => {
    const job = jobs.find((item) => item.key === doc.key);
    if (!job || job.status !== "ready") return doc;
    if (doc.summarized && doc.file === null && doc.extractedText === job.extractedText) return doc;
    changed = true;
    return { ...doc, extractedText: job.extractedText, file: null, summarized: true };
  });

  for (const job of jobs) {
    if (job.status !== "ready" || !job.savedDocumentId) continue;
    if (next.some((doc) => doc.key === job.key)) continue;
    changed = true;
    next.push({
      key: job.key,
      fileName: job.fileName,
      extractedText: job.extractedText,
      file: null,
      summarized: true,
    } as T);
  }

  return changed ? next : docs;
}

/** Saved PDF rows use this key when Summarize replaces them with a pending summary. */
export function savedPdfJobKey(savedDocumentId: string): string {
  return `saved:${savedDocumentId}`;
}

export function savedIdFromReplacementKey(key: string): string | null {
  const prefix = "saved:";
  if (!key.startsWith(prefix)) return null;
  const id = key.slice(prefix.length);
  return id.length > 0 ? id : null;
}

/** Dropping the replacement summary must not omit the stored PDF on save. */
export function removedIdsAfterDiscard(current: Set<string>, pendingKey: string): Set<string> {
  const savedId = savedIdFromReplacementKey(pendingKey);
  if (!savedId || !current.has(savedId)) return current;
  const next = new Set(current);
  next.delete(savedId);
  return next;
}

export function withSavedDocsRemoved(current: Set<string>, jobs: ReadySummaryJob[]): Set<string> {
  let changed = false;
  const next = new Set(current);
  for (const job of jobs) {
    if (job.status === "ready" && job.savedDocumentId && !next.has(job.savedDocumentId)) {
      next.add(job.savedDocumentId);
      changed = true;
    }
  }
  return changed ? next : current;
}
